import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { LabeledComponent, Spinner } from '@@/ui/components';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormField,
  FormItem,
  FormMessage,
  Input
} from '@@/ui/primitives';
import { useCreateEntity } from '../../../hooks/use-entities';
import { toast } from 'sonner';

interface CreateEntityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentId: string;
  isRoot: boolean;
}

type FormValues = {
  name: string;
  description: string;
};

const formSchema = z.object({
  name: z
    .string()
    .min(3, {
      message: 'Entity name must be at least 3 characters.'
    })
    .max(32, {
      message: 'Entity name must be less than 32 characters.'
    }),
  description: z
    .string()
    .min(1, {
      message: 'Description is required.'
    })
    .max(500, {
      message: 'Description must be less than 500 characters.'
    })
}) satisfies z.ZodType<FormValues>;

export function CreateEntityModal({ open, onOpenChange, parentId, isRoot }: CreateEntityModalProps) {
  // const [createLoading, setCreateLoading] = useState(false);
  const { mutate: createEntity, isPending } = useCreateEntity();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: ''
    }
  });

  const handleCreateEntity = async (values: FormValues) => {
    createEntity(
      {
        name: values.name,
        description: values.description,
        parentId: parentId
      },
      {
        onSuccess: () => {
          toast.success('Entity created successfully');
          form.reset();
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error(error.message);
        }
      }
    );
  };

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='font-sans!'>
        <DialogHeader>
          <DialogTitle>Create a new {isRoot ? 'Entity' : 'Sub-Entity'}</DialogTitle>
          <DialogDescription>
            Fill in the details below to create a new {isRoot ? 'Entity' : 'Sub-Entity'}.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleCreateEntity)} className='space-y-6'>
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <LabeledComponent label='Name *'>
                    <Input placeholder='Enter entity name' {...field} className='text-sm!' />
                  </LabeledComponent>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem>
                  <LabeledComponent label='Description *'>
                    <Input placeholder='Enter entity description' {...field} className='text-sm!' />
                  </LabeledComponent>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className='flex justify-end gap-2'>
              <Button type='button' variant='ghost' onClick={handleClose} className='text-sm! font-medium!'>
                Cancel
              </Button>
              <Button type='submit' disabled={isPending} className='text-sm! font-medium!'>
                {isPending ? <Spinner /> : `Create ${isRoot ? 'Entity' : 'Sub-Entity'}`}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
